import React, { useState } from 'react'
import { Globe } from 'lucide-react'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '../hooks/use-i18n'
import { leaveWorldFootball, leaveWorldFormula1, QUERY_KEY_MY_PUBLIC_ENROLLMENTS } from '../services/publicService'

interface LeaveWorldFootballModalProps {
  onClose: () => void
  onSuccess?: () => void
  roomId?: string
  variant?: 'football' | 'formula1'
}

const LeaveWorldFootballModal: React.FC<LeaveWorldFootballModalProps> = ({
  onClose,
  onSuccess,
  roomId,
  variant = 'football',
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      if (variant === 'formula1') {
        await leaveWorldFormula1()
      } else {
        await leaveWorldFootball()
      }
      await queryClient.refetchQueries({ queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS })
      if (roomId) {
        await queryClient.refetchQueries({
          queryKey: variant === 'formula1' ? ['public-formula1-status', roomId] : ['public-football-status', roomId],
        })
      }
      toast.success(t('worldFootball.leave.success'))
      onSuccess?.()
      onClose()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('worldFootball.leave.failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-destructive" />
          <h2 className="font-display text-xl font-bold text-foreground">
            {t('worldFootball.leave.title')}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">{t('worldFootball.leave.body')}</p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
          >
            {t('worldFootball.leave.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading ? t('common.loading') : t('worldFootball.leave.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default LeaveWorldFootballModal
